// Updated uploadAvatarFile to ensure token is always included in headers and handle token refresh

import apiClient from './api.js';



// Upload avatar file directly (for new upload system)
export const uploadAvatarFile = async (file) => {
  try {
    let token = await getValidToken();
    console.log('Avatar upload - using token:', token ? token.substring(0, 20) + '...' : 'null');

    const formData = new FormData();
    formData.append('avatar', file);

    const uploadResponse = await apiClient.post('/api/users/profile/upload', formData, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    console.log('Avatar file upload - response:', uploadResponse.data);
    return uploadResponse.data;
  } catch (error) {
    console.error('Error uploading avatar file:', error);
    console.error('Error response:', error.response?.data);

    // If we get a 403 error, the token might be invalid - try to refresh and retry once
    if (error.response?.status === 403 && error.response?.data?.message === 'Invalid token') {
      console.log('Token appears invalid, clearing and retrying...');

      // Clear the invalid token
      localStorage.removeItem('token');

      try {
        // Get a fresh token
        const freshToken = await getValidToken();
        console.log('Avatar upload retry - using fresh token:', freshToken ? freshToken.substring(0, 20) + '...' : 'null');

        const formData = new FormData();
        formData.append('avatar', file);

        const retryResponse = await apiClient.post('/api/users/profile/upload', formData, {
          headers: {
            Authorization: `Bearer ${freshToken}`
          }
        });

        console.log('Avatar file upload retry - response:', retryResponse.data);
        return retryResponse.data;
      } catch (retryError) {
        console.error('Retry also failed:', retryError);
        throw retryError;
      }
    }

    throw error;
  }
};

// Update user profile with profile image URL
export const updateProfileWithImage = async (profileImageUrl) => {
  try {
    const token = await getValidToken();

    // Use POST to /api/profile-info/update to match backend route
    const response = await apiClient.post('/api/profile-info/update', {
      avatar: profileImageUrl
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

// Upload avatar via URL
export const uploadAvatarUrl = async (avatarUrl) => {
  try {
    const token = await getValidToken();
    console.log('Avatar URL upload - using token:', token ? token.substring(0, 20) + '...' : 'null');

    const response = await apiClient.post('/api/users/profile/upload-url', {
      avatarUrl
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    console.log('Avatar URL upload - response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error uploading avatar URL:', error);
    console.error('Error response:', error.response?.data);

    // If we get a 403 error, the token might be invalid - try to refresh and retry once
    if (error.response?.status === 403 && error.response?.data?.message === 'Invalid token') {
      console.log('Token appears invalid, clearing and retrying...');

      // Clear the invalid token
      localStorage.removeItem('token');

      try {
        // Get a fresh token
        const freshToken = await getValidToken();
        console.log('Avatar URL upload retry - using fresh token:', freshToken ? freshToken.substring(0, 20) + '...' : 'null');

        const retryResponse = await apiClient.post('/api/users/profile/upload-url', {
          avatarUrl
        }, {
          headers: {
            Authorization: `Bearer ${freshToken}`
          }
        });

        console.log('Avatar URL upload retry - response:', retryResponse.data);
        return retryResponse.data;
      } catch (retryError) {
        console.error('Retry also failed:', retryError);
        throw retryError;
      }
    }

    throw error;
  }
};


// Helper to get valid token
async function getValidToken() {
  try {
    // First try to get token from localStorage
    let token = localStorage.getItem('token');

    if (!token) {
      console.warn('No token found in localStorage, trying Firebase auth...');

      // Try to get fresh token from Firebase
      const { auth } = await import('../Firebase.js');
      if (auth.currentUser) {
        try {
          token = await auth.currentUser.getIdToken(true);
          localStorage.setItem('token', token);
          console.log('Got fresh token from Firebase');
          return token;
        } catch (firebaseError) {
          console.error('Error getting Firebase token:', firebaseError);
          throw new Error('Authentication required. Please log in again.');
        }
      }

      throw new Error('Authentication required. Please log in to upload files.');
    }

    // Check if token is close to expiring and try to refresh
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = payload.exp - currentTime;

      // If token expires in less than 5 minutes, try to refresh
      if (timeUntilExpiry < 300) {
        console.log('Token expires soon, attempting refresh...');

        const { auth } = await import('../Firebase.js');
        if (auth.currentUser) {
          const freshToken = await auth.currentUser.getIdToken(true);
          localStorage.setItem('token', freshToken);
          console.log('Token refreshed successfully');
          return freshToken;
        } else {
          throw new Error('Authentication expired. Please log in again.');
        }
      }
    } catch (decodeError) {
      console.warn('Error checking token expiration, using existing token:', decodeError.message);
    }

    return token;
  } catch (error) {
    console.error('Error in getValidToken:', error);
    throw error; // Don't fallback to mock token
  }
}
